import React from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

/**
 * Compact session block for dense calendar day cells — not ResultCard.
 * Full row is the tap target (no separate Select button).
 */
export const CalendarSessionChip = ({
  item = {},
  onClick,
  disabled = false,
  eligibilityChip = null,
}) => {
  const { name, hours, spotsAvailable } = item

  return (
    <Box
      component='button'
      type='button'
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        py: 0.65,
        px: 0.85,
        mx: 0,
        mb: 0,
        mt: 0.5,
        border: '1px solid',
        borderColor: theme =>
          disabled
            ? alpha(theme.palette.divider, 0.9)
            : alpha(theme.palette.primary.main, 0.12),
        borderRadius: 1.5,
        bgcolor: theme =>
          disabled
            ? alpha(
                theme.palette.grey[500],
                theme.palette.mode === 'dark' ? 0.12 : 0.06
              )
            : alpha(theme.palette.primary.main, 0.04),
        cursor: disabled ? 'not-allowed' : 'pointer',
        minWidth: 0,
        opacity: disabled ? 0.55 : 1,
        transition: theme =>
          theme.transitions.create(
            [
              'background-color',
              'border-color',
              'transform',
              'box-shadow',
              'opacity',
            ],
            { duration: theme.transitions.duration.shorter }
          ),
        ...(!disabled
          ? {
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.secondary.main, 0.22),
                borderColor: 'primary.main',
                boxShadow: theme =>
                  `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }
          : {
              '&:hover': {
                bgcolor: theme =>
                  alpha(
                    theme.palette.grey[500],
                    theme.palette.mode === 'dark' ? 0.12 : 0.06
                  ),
              },
            }),
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Typography
        variant='caption'
        fontWeight={700}
        color='text.primary'
        sx={{
          display: 'block',
          lineHeight: 1.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.72rem',
        }}
        title={name}
      >
        {name}
      </Typography>
      {hours ? (
        <Typography
          variant='caption'
          color='text.secondary'
          component='span'
          sx={{
            display: 'block',
            mt: 0.35,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1.35,
            fontWeight: 600,
            fontSize: '0.72rem',
          }}
        >
          {hours}
        </Typography>
      ) : null}
      {spotsAvailable != null && spotsAvailable !== '' ? (
        <Typography
          variant='caption'
          color='primary'
          fontWeight={700}
          sx={{
            display: 'inline-block',
            lineHeight: 1.2,
            mt: 0.45,
            px: 0.65,
            py: 0.15,
            borderRadius: 1,
            bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
            fontSize: '0.65rem',
          }}
        >
          {spotsAvailable} open
        </Typography>
      ) : null}
      {eligibilityChip ? (
        <Chip
          size='small'
          label={eligibilityChip.label}
          color={eligibilityChip.status === 'eligible' ? 'success' : 'default'}
          variant={
            eligibilityChip.status === 'eligible' ? 'filled' : 'outlined'
          }
          sx={{
            display: 'flex',
            mt: 0.5,
            maxWidth: '100%',
            height: 22,
            fontSize: '0.62rem',
            fontWeight: 700,
            '& .MuiChip-label': { px: 0.65, py: 0, overflow: 'hidden' },
          }}
        />
      ) : null}
    </Box>
  )
}

CalendarSessionChip.propTypes = {
  item: PropTypes.object,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  eligibilityChip: PropTypes.shape({
    label: PropTypes.string,
    status: PropTypes.oneOf(['eligible', 'not_eligible']),
  }),
}
